import axios from 'axios';
import axiosInstance from './axiosInstance';
import config from '../config/config';
import securityConfig from '../config/securityConfig';
import { authChannel } from '../utils/authChannel';
import api from './api';

// Constants
const TOKEN_STORAGE_KEY = 'accessToken';
const REFRESH_TOKEN_STORAGE_KEY = 'refreshToken';
const USER_ID_KEY = 'userId';
const CSRF_TOKEN_KEY = 'csrfToken';

// Auth state
let inMemoryToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;
let isRefreshing = false;
let refreshTimerId: number | null = null;

// Response types
export interface AuthResponse {
  userId: string;
  email: string;
  username: string;
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  emailVerified: boolean;
  message?: string;
  newUser?: boolean;
  csrfToken?: string;
  profilePictureUrl?: string;
  profileImageUrl?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  fullName: string;
  email: string;
  password: string;
}

/**
 * Gets the current access token from memory or storage
 * @returns The access token or null
 */
export const getAccessToken = (): string | null => {
  // First check in-memory token (fastest)
  if (inMemoryToken) {
    // console.log("Using in-memory token");
    return inMemoryToken;
  }
  
  // Then check localStorage
  const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (storedToken) {
    // Store in memory for future use
    console.log("Retrieved token from localStorage");
    inMemoryToken = storedToken;
    return storedToken;
  }
  
  console.warn("No token found in memory or localStorage");
  return null;
};

/**
 * Parses a JWT token and returns the payload
 */
export const parseToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch (error) {
    console.error('Error parsing token:', error);
    return null;
  }
};

/**
 * Checks if a token is expired
 * @param token The token to check
 * @param bufferSeconds Buffer time before actual expiration (default: 60 seconds)
 */
export const isTokenExpired = (token: string, bufferSeconds = 60): boolean => {
  try {
    const payload = parseToken(token);
    if (!payload || !payload.exp) return true;
    
    // exp is in seconds, Date.now() is in milliseconds
    return (payload.exp - bufferSeconds) * 1000 < Date.now();
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // Assume expired if we can't parse it
  }
};

/**
 * Gets the expiration time of a token in milliseconds
 */
export const getTokenExpirationTime = (token: string): number | null => {
  try {
    const payload = parseToken(token);
    if (!payload || !payload.exp) return null;
    
    return payload.exp * 1000; // Convert to milliseconds
      } catch (error) {
    console.error('Error getting token expiration:', error);
    return null;
  }
};

/**
 * Calculates the time until a token expires
 */
export const getTimeUntilExpiration = (token: string): number | null => {
  const expirationTime = getTokenExpirationTime(token);
  if (!expirationTime) return null;
  
  return Math.max(0, expirationTime - Date.now());
};

/**
 * Sets the access token in memory and storage
 */
export const setAccessToken = (token: string | null): void => {
  if (token) {
    inMemoryToken = token;
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    
    // Update axios headers
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Schedule token refresh before expiration
    scheduleTokenRefresh(token);
    
  } else {
    inMemoryToken = null;
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    
    // Clear axios headers
    delete axios.defaults.headers.common['Authorization'];
    delete axiosInstance.defaults.headers.common['Authorization'];
    
    // Cancel any scheduled refresh
    if (refreshTimerId !== null) {
      window.clearTimeout(refreshTimerId);
      refreshTimerId = null;
    }
  }
  
  // Notify that token has changed
  window.dispatchEvent(new Event('tokenChange'));
};

/**
 * Sets up a timer to refresh the token before it expires
 */
export const scheduleTokenRefresh = (token: string): void => {
  // Cancel any existing refresh timer
  if (refreshTimerId !== null) {
    window.clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
  
  // Only schedule refresh if we have a valid token
  const timeUntilExpiration = getTimeUntilExpiration(token);
  if (!timeUntilExpiration) return;
  
  // Calculate when to refresh (80% of token lifetime)
  const refreshBuffer = securityConfig.tokenRefresh.refreshBeforeExpiry;
  const refreshTime = Math.max(timeUntilExpiration - refreshBuffer, 1000); // At least 1 second
  
  console.log(`Token refresh scheduled in ${Math.round(refreshTime / 1000)} seconds`);
  
  // Schedule the refresh
  refreshTimerId = window.setTimeout(() => {
    refreshToken().catch(error => {
      console.error('Scheduled token refresh failed:', error);
    });
  }, refreshTime);
};

/**
 * Checks if the user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = getAccessToken();
  return !!token && !isTokenExpired(token);
};

/**
 * Stores the CSRF token in localStorage for cross-domain access
 */
export const setCsrfToken = (token: string): void => {
  if (!token) return;
  
  // Store in localStorage instead of sessionStorage for persistence and cross-domain access
  localStorage.setItem(CSRF_TOKEN_KEY, token);
  console.log('CSRF token stored in localStorage');
    
    // Update axios headers
  axios.defaults.headers.common[securityConfig.csrfHeaderName] = token;
  axiosInstance.defaults.headers.common[securityConfig.csrfHeaderName] = token;
};

/**
 * Gets the CSRF token from localStorage
 * The cookie fallback is kept but deprioritized since it won't work cross-domain
 */
export const getCsrfToken = (): string | null => {
  // First try to get from localStorage
  const localToken = localStorage.getItem(CSRF_TOKEN_KEY);
  if (localToken) {
    return localToken;
  }
  
  // Fallback: try to extract from cookies (likely won't work cross-domain)
  try {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${securityConfig.cookies.csrfCookie}=`))
      ?.split('=')[1];
      
    if (cookieValue) {
      // Store in localStorage for future use
      localStorage.setItem(CSRF_TOKEN_KEY, cookieValue);
      return cookieValue;
    }
  } catch (error) {
    console.error('Error extracting CSRF token from cookies:', error);
  }
  
  return null;
};

/**
 * The current implementation does not have a dedicated CSRF token endpoint.
 * Instead, CSRF tokens are included in the response to auth operations (login, register, refresh).
 * This function simply returns the existing token if available.
 */
export const fetchCsrfToken = async (): Promise<string | null> => {
  // Check if we already have a CSRF token
  const existingToken = getCsrfToken();
  if (existingToken) {
    console.log('Using existing CSRF token from localStorage');
    return existingToken;
  }
  
  console.warn('No existing CSRF token found in localStorage');
  console.warn('The system does not have a dedicated CSRF token endpoint.');
  console.warn('CSRF tokens are only available after authentication operations (login/register/refresh).');
  
  // Try to refresh the token which might give us a new CSRF token
  if (isAuthenticated()) {
    console.log('Attempting to refresh auth token to get a new CSRF token...');
    await refreshToken();
    
    // Check if we got a CSRF token from the refresh operation
    const newToken = getCsrfToken();
    if (newToken) {
      console.log('Successfully obtained CSRF token via token refresh');
      return newToken;
    }
  }
  
  return null;
};

/**
 * Refreshes the access token using the refresh token
 * Uses a promise cache to prevent multiple concurrent refresh requests
 */
export const refreshToken = async (): Promise<string | null> => {
  // Return existing promise if already refreshing
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  
  // Check if we should even try to refresh
  const currentToken = getAccessToken();
  if (!currentToken) {
    console.log('No token to refresh');
    return null;
  }
  
  isRefreshing = true;
  
  // Create a new refresh promise
  refreshPromise = (async () => {
    try {
      console.log('Refreshing token...');
      
      // Try to get CSRF token - but don't fail if not available
      // Use the sync version to avoid Promise in header issues
      const csrfToken = getCsrfToken();
      
      if (!csrfToken) {
        console.warn('No CSRF token in localStorage, will try to proceed without it');
      } else {
        console.log(`Using CSRF token for refresh: ${csrfToken}`);
      }
      
      // Create headers with available information
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}` // Include current token in header
      };
      
      // Add CSRF token if available
      if (csrfToken) {
        headers[securityConfig.csrfHeaderName] = csrfToken;
      }
      
      // Try the refresh with available tokens
      try {
        console.log(`Calling refresh endpoint: ${config.apiUrl}${securityConfig.endpoints.refresh}`);
        const response = await axios.post<AuthResponse>(
          `${config.apiUrl}${securityConfig.endpoints.refresh}`,
          {}, // Empty request body
          {
            headers,
            withCredentials: true // Important for cross-domain cookie handling
          }
        );
        
        if (response.data && response.data.accessToken) {
          console.log('Token refreshed successfully, initializing tokens...');
          
          // Initialize tokens from response data for cross-domain support
          initializeTokensFromResponse(response.data, response.headers);
          
          return response.data.accessToken;
        } else {
          console.warn('Refresh response did not contain a new access token');
          return null;
        }
      } catch (error) {
        // If we get a 403 (Forbidden), it might be due to missing CSRF token
        if (error.response?.status === 403) {
          console.log('Refresh failed with 403 status, likely CSRF validation error');
          
          // At this point, we don't have a dedicated CSRF endpoint, so we should clear auth state
          console.log('Cannot proceed without CSRF token, clearing auth state');
          setAccessToken(null);
          
          // Notify app that authentication has been lost
          window.dispatchEvent(new Event('authExpired'));
          
          return null;
        }
        
        // Re-throw the original error if we couldn't recover
        throw error;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      console.error('Error details:', error.response?.status, error.response?.data);
      
      // If we get a 401 or 403, the refresh token is invalid or CSRF failed
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Refresh token expired or CSRF validation failed, clearing auth state');
        setAccessToken(null);
        
        // Notify app that authentication has been lost
        window.dispatchEvent(new Event('authExpired'));
      }
      
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
};

/**
 * Logs the user in with email and password
 */
export const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  try {
    const response = await axios.post<AuthResponse>(
      `${config.apiUrl}${securityConfig.endpoints.login}`,
      credentials,
      {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      }
    );
    
    if (response.data && response.data.accessToken) {
      console.log('Login successful, initializing tokens');
      
      // Use the comprehensive token initialization function
      initializeTokensFromResponse(response.data, response.headers);
    } else {
      console.warn('Login response missing access token');
    }

    return response.data;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

/**
 * Registers a new user
 */
export const register = async (credentials: RegisterCredentials): Promise<AuthResponse> => {
  try {
    console.log('Attempting registration with credentials:', { 
      ...credentials, 
      password: '[REDACTED]' 
    });
    
    const response = await axios.post<AuthResponse>(
      `${config.apiUrl}${securityConfig.endpoints.register}`,
      credentials,
      {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      }
    );
    
    console.log('Registration response status:', response.status);
    console.log('Registration response headers:', response.headers);
    
    if (response.data) {
      console.log('Registration successful, processing response data');
      
      if (response.data.accessToken) {
        console.log('Access token found in response, initializing tokens');
        
        // Initialize tokens with comprehensive error handling
        try {
          initializeTokensFromResponse(response.data, response.headers);
          console.log('Token initialization successful');
        } catch (tokenError) {
          console.error('Failed to initialize tokens:', tokenError);
          // Continue with registration process even if token init fails
          // The user will need to log in again
        }
      } else {
        console.warn('No access token in registration response - user may need to verify email first');
      }
      
      // Always return the response data for the component to handle
      return response.data;
    } else {
      throw new Error('Invalid response format from registration endpoint');
    }
  } catch (error) {
    console.error('Registration failed:', error);
    console.error('Error response:', error.response?.data);
    
    // Enhance error object with more details if available
    if (error.response?.data) {
      error.details = error.response.data;
    }
    
    throw error;
  }
};

/**
 * Logs the user out
 */
export const logout = async (): Promise<boolean> => {
  try {
    // Broadcast logout to other tabs BEFORE making API call
    // This ensures all tabs respond immediately
    authChannel.broadcastLogout();

    try {
      // Call logout endpoint to invalidate tokens on server
      await axios.post(
        `${config.apiUrl}${securityConfig.endpoints.logout}`,
        {},
        {
        headers: {
            'Content-Type': 'application/json',
            [securityConfig.csrfHeaderName]: getCsrfToken() || ''
          },
          withCredentials: true
        }
      );
    } catch (error) {
      console.error('Error calling logout endpoint:', error);
      // Continue with client-side logout even if server call fails
    }
    
    // Clear all auth data
    clearAuthData();
    
    return true;
  } catch (error) {
    console.error('Logout error:', error);
    
    // Still try to clear tokens even if there was an error
    clearAuthData();
    
    return false;
  }
};

/**
 * Clears all authentication data
 */
export const clearAuthData = (): void => {
  console.log('Clearing all authentication data');
  
  // Clear tokens
  setAccessToken(null);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(CSRF_TOKEN_KEY);
  
  // Clear auth headers
  delete axios.defaults.headers.common['Authorization'];
  delete axios.defaults.headers.common[securityConfig.csrfHeaderName];
  delete axiosInstance.defaults.headers.common['Authorization'];
  delete axiosInstance.defaults.headers.common[securityConfig.csrfHeaderName];
  
  // Clear all auth-related cookies
  clearAuthCookies();
  
  // Dispatch event
      window.dispatchEvent(new Event('tokenChange'));
};

/**
 * Clears authentication cookies across all possible paths and domains
 */
export const clearAuthCookies = (): void => {
  const cookieNames = [
    securityConfig.cookies.accessTokenCookie,
    securityConfig.cookies.refreshTokenCookie,
    securityConfig.cookies.csrfCookie
  ];
  
  const paths = securityConfig.cookies.paths;
  const domains = ['', window.location.hostname];
  
  // If we're on a subdomain, also clear cookies on the parent domain
  if (window.location.hostname.split('.').length > 2) {
    const parts = window.location.hostname.split('.');
    const parentDomain = `.${parts.slice(-2).join('.')}`;
    domains.push(parentDomain);
  }
  
  // Clear each cookie with various combinations
  cookieNames.forEach(name => {
    domains.forEach(domain => {
    paths.forEach(path => {
        const domainPart = domain ? `; domain=${domain}` : '';
        document.cookie = `${name}=; path=${path}${domainPart}; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
      });
    });
  });
};

/**
 * Initializes the authentication state from storage
 */
export const initializeAuth = (): void => {
  console.log("Initializing auth state...");
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  
  if (token) {
    console.log("Found token in localStorage");
    // Check if token is expired
    if (isTokenExpired(token)) {
      console.log('Stored token is expired, attempting to refresh');
      refreshToken().catch((error) => {
        console.error("Token refresh failed during initialization:", error);
        // If refresh fails, clear the token
        setAccessToken(null);
      });
    } else {
      console.log('Found valid token, storing in memory');
      // Valid token, store in memory
      setAccessToken(token);
    }
  } else {
    console.log("No token found in localStorage during initialization");
  }
  
  // Set up listeners for auth events from other tabs
  authChannel.listen(
    // Logout handler
    () => {
      console.log('Received logout event from another tab');
      clearAuthData();
    },
    // Login handler
    () => {
      console.log('Received login event from another tab');
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
        setAccessToken(token);
      }
    }
  );
};

/**
 * Gets the user ID from storage
 */
export const getUserId = (): string | null => {
  return localStorage.getItem(USER_ID_KEY);
};

/**
 * Handles OAuth authentication callback
 */
export const handleOAuthCallback = (token: string, csrfToken?: string): boolean => {
  try {
    if (!token) {
      console.error('No token provided in OAuth callback');
      return false;
    }
    
    console.log('OAuth callback received token');
    
    // Create a dummy response object for token initialization
    const dummyResponse: AuthResponse = {
      userId: '',  // Will be extracted from token below
      email: '',
      username: '',
      accessToken: token,
      emailVerified: true,
      csrfToken: csrfToken
    };
    
    // Extract user ID from token
    const payload = parseToken(token);
    if (payload?.sub || payload?.userId) {
      dummyResponse.userId = payload.userId || payload.sub;
    }
    
    // Use the comprehensive token initialization function
    initializeTokensFromResponse(dummyResponse, {});
      
      return true;
  } catch (error) {
    console.error('OAuth callback error:', error);
    return false;
  }
};

/**
 * Sign in with Google
 */
export const signInWithGoogle = (): void => {
  // Generate state parameter to prevent CSRF
  const state = Math.random().toString(36).substring(2);
  localStorage.setItem('oauth_state', state);
  
  // Store current location for redirect after login ONLY if not already set
  // This ensures that intent from the Login/SignIn components is respected
  if (!localStorage.getItem('redirect_after_login')) {
    localStorage.setItem('redirect_after_login', window.location.pathname);
  } else {
    console.log('Using existing redirect path:', localStorage.getItem('redirect_after_login'));
  }
  
  // Build the OAuth URL
  // Use the oauthUrl instead of apiUrl + endpoint path to match SignIn implementation
  const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);
  
  // Debug logging
  console.log('Using oauthUrl:', config.oauthUrl);
  console.log('Redirect URI:', redirectUri);
  
  // Use the same format as in SignIn.tsx (config.oauthUrl/authorization/google)
  // This fixes the "No static resource api/v1/auth/oauth2/google" error
  const googleAuthUrl = `${config.oauthUrl}/authorization/google?redirect_uri=${redirectUri}&state=${state}`;
  console.log('Google Auth URL:', googleAuthUrl);
  
  // Redirect to Google login
  window.location.href = googleAuthUrl;
};

/**
 * Initializes tokens from API response instead of cookies
 * Used in cross-domain scenarios where cookies aren't accessible
 * @param responseData The response data from refresh endpoint
 * @param responseHeaders Headers from the refresh response
 */
export const initializeTokensFromResponse = (
  responseData: AuthResponse, 
  responseHeaders: any
): void => {
  console.log('Initializing tokens from response data');
  
  // Set the access token from response body
  if (responseData.accessToken) {
    console.log('Setting access token from response body');
    setAccessToken(responseData.accessToken);
  } else {
    console.warn('No access token found in response data');
  }
  
  // Get CSRF token with fallbacks:
  // 1. Try response body (most reliable in cross-domain scenarios)
  let csrfToken = responseData.csrfToken;
  let csrfSource = 'response body';
  
  // 2. Try response header if not in body
  if (!csrfToken) {
    const headerName = securityConfig.csrfHeaderName.toLowerCase();
    csrfToken = responseHeaders?.[headerName] as string;
    if (csrfToken) {
      csrfSource = 'header';
    }
  }
  
  // Store CSRF token if found (no fallback to cookies since they don't work cross-domain)
  if (csrfToken) {
    console.log(`Setting CSRF token from ${csrfSource}`);
    setCsrfToken(csrfToken);
    } else {
    console.warn('No CSRF token found in response - cross-domain requests requiring CSRF will fail');
    
    // Use existing token if available as last resort
    const existingToken = getCsrfToken();
    if (existingToken) {
      console.log('Using existing CSRF token from storage');
    }
  }
  
  // Store user ID if available
  if (responseData.userId) {
    console.log('Setting user ID from response data');
    localStorage.setItem(USER_ID_KEY, responseData.userId);
  }
  
  // Broadcast login to other tabs
  authChannel.broadcastLogin();
};

// Auto-initialize auth when the module is loaded
initializeAuth();

// Make debugging easier in development
if (process.env.NODE_ENV === 'development') {
  (window as any).__authDebug = {
    getAccessToken,
    isAuthenticated,
    parseToken,
    refreshToken,
    isTokenExpired,
    getCsrfToken
  };
}
